import React, { useState } from 'react';
import {
  ShieldCheckIcon,
  XMarkIcon,
  CheckIcon,
  TruckIcon,
  CubeIcon,
  CalendarIcon,
  UserIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  UserGroupIcon,
  AcademicCapIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '../../services/authService';

export interface SolicitudOperacionAlmacen {
  id_solicitud: string;
  tipo: 'INGRESO' | 'SALIDA';
  estado: 'PENDIENTE' | 'APROBADA' | 'RECHAZADA';
  sku: string;
  nombre: string;
  cantidad: number;
  unidad_medida: string;
  ubicacion?: string;
  id_producto?: number;
  datos?: any;
  solicitante_id?: number | string;
  solicitante_nombre?: string;
  fecha: string;
}

interface MiembroSimple {
  id_miembro?: number;
  id_usuario?: number;
  nombre: string;
  apellido: string;
  email: string;
  rol_miembro?: string;
  turno?: string;
  avatar?: string;
}

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  solicitudes: SolicitudOperacionAlmacen[];
  onActualizar: () => Promise<void>;
  requiereAprobacion: boolean;
  onCambiarPolitica: (requiere: boolean) => Promise<void>;
  esGestor: boolean;
  miembros?: MiembroSimple[];
  supervisoresDelegadosIds?: number[];
  onActualizarSupervisoresDelegados?: (ids: number[]) => Promise<void>;
}

export const ModalMesaControlAlmacen: React.FC<Props> = ({
  abierto,
  onCerrar,
  solicitudes,
  onActualizar,
  requiereAprobacion,
  onCambiarPolitica,
  esGestor,
  miembros = [],
  supervisoresDelegadosIds = [],
  onActualizarSupervisoresDelegados
}) => {
  const [tabPrincipal, setTabPrincipal] = useState<'OPERACIONES' | 'ROLES_ALUMNOS'>('OPERACIONES');
  const [filtroTipo, setFiltroTipo] = useState<'TODAS' | 'INGRESO' | 'SALIDA'>('TODAS');
  const [estudianteFiltro, setEstudianteFiltro] = useState<string>('TODOS');
  const [procesandoId, setProcesandoId] = useState<string | null>(null);
  const [procesandoLote, setProcesandoLote] = useState(false);
  const [cambiandoPolitica, setCambiandoPolitica] = useState(false);
  const [guardandoSupervisores, setGuardandoSupervisores] = useState(false);
  const [solicitudParaRechazar, setSolicitudParaRechazar] = useState<SolicitudOperacionAlmacen | null>(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [supervisoresLocales, setSupervisoresLocales] = useState<number[]>(supervisoresDelegadosIds);

  React.useEffect(() => {
    setSupervisoresLocales(supervisoresDelegadosIds);
  }, [supervisoresDelegadosIds]);

  if (!abierto) return null;

  // Filtrado de solicitudes por tipo y por estudiante seleccionado
  const solicitudesFiltradas = solicitudes.filter(s => {
    if (filtroTipo !== 'TODAS' && s.tipo !== filtroTipo) return false;
    if (estudianteFiltro !== 'TODOS') {
      const matchId = String(s.solicitante_id || '') === estudianteFiltro;
      const matchNombre = (s.solicitante_nombre || '').toLowerCase().includes(estudianteFiltro.toLowerCase());
      if (!matchId && !matchNombre) return false;
    }
    return true;
  });

  const contarIngresos = solicitudes.filter(s => s.tipo === 'INGRESO').length;
  const contarSalidas = solicitudes.filter(s => s.tipo === 'SALIDA').length;

  // Lista única de solicitantes detectados en las operaciones
  const solicitantesDetectados = Array.from(
    new Set(
      solicitudes
        .map(s => s.solicitante_nombre?.trim())
        .filter((n): n is string => Boolean(n))
    )
  );

  const handleTogglePolitica = async () => {
    if (!esGestor) {
      toast.error('Solo los docentes y supervisores pueden modificar la política de control.');
      return;
    }
    setCambiandoPolitica(true);
    try {
      await onCambiarPolitica(!requiereAprobacion);
    } catch {
      toast.error('Error al actualizar la política');
    } finally {
      setCambiandoPolitica(false);
    }
  };

  const handleAprobarIndividual = async (id: string) => {
    setProcesandoId(id);
    try {
      const res = await api.post(`/api/institucion/almacen/solicitudes/${id}/aprobar`);
      toast.success(res.data?.message || 'Operación aprobada y asentada en inventario', {
        icon: '✅',
        duration: 4000
      });
      await onActualizar();
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Error al aprobar la operación';
      toast.error(msg);
    } finally {
      setProcesandoId(null);
    }
  };

  const handleConfirmarRechazo = async () => {
    if (!solicitudParaRechazar) return;
    setProcesandoId(solicitudParaRechazar.id_solicitud);
    try {
      await api.post(`/api/institucion/almacen/solicitudes/${solicitudParaRechazar.id_solicitud}/rechazar`, {
        motivo: motivoRechazo.trim() || 'No cumple con las especificaciones del ejercicio WMS'
      });
      toast.success('Solicitud rechazada y notificada al registro de auditoría', { icon: '🛡️' });
      setSolicitudParaRechazar(null);
      setMotivoRechazo('');
      await onActualizar();
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Error al rechazar solicitud';
      toast.error(msg);
    } finally {
      setProcesandoId(null);
    }
  };

  const handleAprobarLote = async () => {
    if (solicitudesFiltradas.length === 0) return;
    const msg = estudianteFiltro !== 'TODOS'
      ? `¿Aprobar las ${solicitudesFiltradas.length} operaciones del estudiante '${estudianteFiltro}' y asentarlas en el inventario?`
      : `¿Aprobar todas las ${solicitudes.length} operaciones pendientes y asentarlas en el inventario?`;

    if (!window.confirm(msg)) return;

    setProcesandoLote(true);
    try {
      if (estudianteFiltro === 'TODOS') {
        const res = await api.post('/api/institucion/almacen/solicitudes/aprobar-todas');
        toast.success(res.data?.message || 'Todas las solicitudes fueron aprobadas con éxito.', {
          icon: '⚡',
          duration: 5000
        });
      } else {
        // Aprobar una a una las de este estudiante
        for (const sol of solicitudesFiltradas) {
          await api.post(`/api/institucion/almacen/solicitudes/${sol.id_solicitud}/aprobar`);
        }
        toast.success(`Se aprobaron las ${solicitudesFiltradas.length} solicitudes del estudiante con éxito.`, {
          icon: '✅',
          duration: 4000
        });
      }
      await onActualizar();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Error al procesar la aprobación';
      toast.error(errorMsg);
    } finally {
      setProcesandoLote(false);
    }
  };

  const handleToggleSupervisorEstudiante = async (idUsuario: number) => {
    if (!esGestor) {
      toast.error('Solo los docentes pueden delegar roles de supervisor a estudiantes');
      return;
    }
    const yaEs = supervisoresLocales.includes(idUsuario);
    const nuevos = yaEs
      ? supervisoresLocales.filter(id => id !== idUsuario)
      : [...supervisoresLocales, idUsuario];

    setSupervisoresLocales(nuevos);
    setGuardandoSupervisores(true);
    try {
      await api.post('/api/institucion/almacen/supervisores-delegados', {
        supervisores_ids: nuevos
      });
      if (onActualizarSupervisoresDelegados) {
        await onActualizarSupervisoresDelegados(nuevos);
      }
      toast.success(
        yaEs
          ? 'Rol de supervisor retirado al estudiante.'
          : 'Estudiante promovido a Supervisor de Mesa de Control (puede validar entradas/salidas).',
        { icon: yaEs ? 'ℹ️' : '⭐' }
      );
    } catch {
      toast.error('Error al guardar el rol delegado');
    } finally {
      setGuardandoSupervisores(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-2 sm:p-5 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-5xl max-h-[94vh] flex flex-col rounded-3xl border border-slate-200/80 bg-white shadow-2xl dark:border-white/10 dark:bg-[#0c0d12] overflow-hidden">
        
        {/* Header Modal */}
        <div className="flex items-center justify-between px-6 py-4.5 border-b border-slate-200/70 dark:border-white/10 bg-slate-50/70 dark:bg-white/[0.02]">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 shadow-xs">
              <ShieldCheckIcon className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white">
                  Mesa de Control & Auditoría WMS
                </h3>
                <span className="inline-flex items-center rounded-md bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300 border border-amber-500/20">
                  {solicitudes.length} Pendiente{solicitudes.length === 1 ? '' : 's'}
                </span>
                {supervisoresLocales.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold text-indigo-700 dark:text-indigo-300 border border-indigo-500/20">
                    <StarIcon className="w-3 h-3 text-amber-400 fill-amber-400" />
                    {supervisoresLocales.length} Alumno{supervisoresLocales.length === 1 ? '' : 's'} Supervisor{supervisoresLocales.length === 1 ? '' : 'es'}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                Supervisión pedagógica, filtrado por estudiante y asignación de roles de control operativo.
              </p>
            </div>
          </div>

          <button
            onClick={onCerrar}
            className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-300 transition-colors cursor-pointer"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Pestañas de Vista: Operaciones vs Roles Estudiantiles */}
        <div className="px-6 pt-3 border-b border-slate-200/50 dark:border-white/[0.06] bg-slate-100/40 dark:bg-zinc-900/30 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTabPrincipal('OPERACIONES')}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 text-xs font-bold transition-colors cursor-pointer ${
                tabPrincipal === 'OPERACIONES'
                  ? 'border-amber-500 text-amber-700 dark:text-amber-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200'
              }`}
            >
              <CubeIcon className="w-4 h-4" />
              <span>Solicitudes de Operación ({solicitudes.length})</span>
            </button>

            {esGestor && (
              <button
                onClick={() => setTabPrincipal('ROLES_ALUMNOS')}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 text-xs font-bold transition-colors cursor-pointer ${
                  tabPrincipal === 'ROLES_ALUMNOS'
                    ? 'border-indigo-500 text-indigo-700 dark:text-indigo-300'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200'
                }`}
              >
                <UserGroupIcon className="w-4 h-4" />
                <span>Elegir Alumnos Supervisores ({supervisoresLocales.length})</span>
              </button>
            )}
          </div>

          {/* Toggle de Política */}
          <div className="flex items-center gap-2.5 pb-2 sm:pb-0">
            <button
              type="button"
              disabled={!esGestor || cambiandoPolitica}
              onClick={handleTogglePolitica}
              className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                requiereAprobacion ? 'bg-amber-500' : 'bg-slate-300 dark:bg-zinc-700'
              } ${(!esGestor || cambiandoPolitica) ? 'opacity-60 cursor-not-allowed' : ''}`}
              title={esGestor ? 'Activar o desactivar validación obligatoria' : 'Solo docentes'}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition duration-200 ease-in-out ${
                  requiereAprobacion ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className="text-[11px] font-bold text-slate-700 dark:text-zinc-300">
              {requiereAprobacion ? 'Auditoría Obligatoria' : 'Modo Libre'}
            </span>
          </div>
        </div>

        {tabPrincipal === 'OPERACIONES' ? (
          <>
            {/* Barra de Filtros: Por Tipo y Por Estudiante */}
            <div className="px-6 py-3 border-b border-slate-200/50 dark:border-white/[0.04] bg-white dark:bg-[#0c0d12] flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Selector de Tipo */}
                <div className="flex items-center gap-1 bg-slate-100 dark:bg-zinc-800/60 p-1 rounded-xl">
                  <button
                    onClick={() => setFiltroTipo('TODAS')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                      filtroTipo === 'TODAS'
                        ? 'bg-white text-slate-900 shadow-xs dark:bg-zinc-700 dark:text-white'
                        : 'text-slate-600 dark:text-zinc-400'
                    }`}
                  >
                    Todas ({solicitudes.length})
                  </button>
                  <button
                    onClick={() => setFiltroTipo('INGRESO')}
                    className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                      filtroTipo === 'INGRESO'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-slate-600 dark:text-zinc-400'
                    }`}
                  >
                    <CubeIcon className="w-3.5 h-3.5" />
                    Ingresos ({contarIngresos})
                  </button>
                  <button
                    onClick={() => setFiltroTipo('SALIDA')}
                    className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                      filtroTipo === 'SALIDA'
                        ? 'bg-rose-600 text-white shadow-xs'
                        : 'text-slate-600 dark:text-zinc-400'
                    }`}
                  >
                    <TruckIcon className="w-3.5 h-3.5" />
                    Retiros ({contarSalidas})
                  </button>
                </div>

                {/* Selector para Elegir y Filtrar por Estudiante */}
                <div className="flex items-center gap-1.5 ml-1">
                  <AcademicCapIcon className="w-4 h-4 text-indigo-500 shrink-0" />
                  <select
                    value={estudianteFiltro}
                    onChange={(e) => setEstudianteFiltro(e.target.value)}
                    className="text-xs font-bold rounded-xl border border-slate-300 bg-white py-1.5 pl-2.5 pr-8 text-slate-700 outline-none focus:border-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 cursor-pointer shadow-xs"
                  >
                    <option value="TODOS">👨‍🎓 Todos los Estudiantes ({solicitudes.length})</option>
                    {solicitantesDetectados.map(nombre => {
                      const count = solicitudes.filter(s => s.solicitante_nombre?.trim() === nombre).length;
                      return (
                        <option key={nombre} value={nombre}>
                          {nombre} ({count} solicitud{count === 1 ? '' : 'es'})
                        </option>
                      );
                    })}
                    {miembros
                      .filter(m => !solicitantesDetectados.includes(`${m.nombre} ${m.apellido}`.trim()))
                      .map(m => {
                        const nom = `${m.nombre} ${m.apellido}`.trim();
                        return (
                          <option key={m.id_usuario || m.id_miembro} value={nom}>
                            {nom} (0 pendientes)
                          </option>
                        );
                      })}
                  </select>
                </div>
              </div>

              {/* Botón de Aprobación en Lote */}
              {esGestor && solicitudesFiltradas.length > 0 && (
                <button
                  onClick={handleAprobarLote}
                  disabled={procesandoLote}
                  className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 cursor-pointer"
                >
                  <SparklesIcon className="w-3.5 h-3.5" />
                  <span>
                    {procesandoLote
                      ? 'Aprobando...'
                      : estudianteFiltro !== 'TODOS'
                      ? `Aprobar ${solicitudesFiltradas.length} de ${estudianteFiltro}`
                      : `Aprobar Todas (${solicitudesFiltradas.length})`}
                  </span>
                </button>
              )}
            </div>

            {/* Lista de Solicitudes */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
              {solicitudesFiltradas.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-100 dark:bg-zinc-800/60 mb-3 text-slate-400 dark:text-zinc-600">
                    <CheckCircleIcon className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-800 dark:text-zinc-200">
                    {estudianteFiltro !== 'TODOS'
                      ? `No hay solicitudes pendientes para '${estudianteFiltro}'`
                      : 'Mesa de Control al día'}
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-zinc-500 max-w-sm mt-1">
                    Todas las operaciones de entrada y salida están asentadas y sincronizadas con el inventario físico.
                  </p>
                </div>
              ) : (
                solicitudesFiltradas.map((sol) => {
                  const esIngreso = sol.tipo === 'INGRESO';
                  const datos = sol.datos || {};
                  const destinoTipo = datos.tipo_destino || datos.tipoEntrada || (datos.contenedor_numero ? 'CONTENEDOR' : 'ALMACEN');
                  const esContenedor = destinoTipo === 'CONTENEDOR';

                  return (
                    <div
                      key={sol.id_solicitud}
                      className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-xs hover:shadow-md transition-all dark:border-white/[0.08] dark:bg-[#121319]"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 border-b border-slate-100 dark:border-white/[0.05] pb-3.5">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                              esIngreso
                                ? 'bg-blue-500/10 text-blue-600 border border-blue-500/20 dark:text-blue-400'
                                : 'bg-rose-500/10 text-rose-600 border border-rose-500/20 dark:text-rose-400'
                            }`}
                          >
                            {esIngreso ? <CubeIcon className="w-3.5 h-3.5" /> : <TruckIcon className="w-3.5 h-3.5" />}
                            {esIngreso ? 'Ingreso al Almacén' : 'Retiro de Mercancía'}
                          </span>

                          <span className="font-mono text-xs font-bold text-slate-500 dark:text-zinc-400">
                            {sol.id_solicitud}
                          </span>

                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-md border border-indigo-200/60 dark:border-indigo-500/30">
                            {esContenedor ? '🚢 Modo Contenedor' : '🏢 Modo Almacén'}
                          </span>
                        </div>

                        {/* Identificación del Estudiante */}
                        <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400 text-xs">
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-zinc-800/80 border border-slate-200/60 dark:border-zinc-700">
                            <UserIcon className="w-3.5 h-3.5 text-indigo-500" />
                            <span className="font-bold text-slate-900 dark:text-white">
                              {sol.solicitante_nombre || 'Estudiante'}
                            </span>
                          </div>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-[11px]">
                            <CalendarIcon className="w-3.5 h-3.5" />
                            {new Date(sol.fecha).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>

                      {/* Detalle del Producto */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-3.5">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                            Producto & SKU
                          </p>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                            {sol.nombre || 'Mercancía'}
                          </h4>
                          <p className="font-mono text-xs font-bold text-sky-600 dark:text-sky-400">
                            SKU: {sol.sku}
                          </p>
                        </div>

                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                            Cantidad & Ubicación
                          </p>
                          <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                            {sol.cantidad} {sol.unidad_medida || 'unidades'}
                          </p>
                          <p className="text-xs font-mono text-slate-600 dark:text-zinc-400 flex items-center gap-1 mt-0.5">
                            <MapPinIcon className="w-3.5 h-3.5 text-slate-400" />
                            {sol.ubicacion || 'Zona General'}
                          </p>
                        </div>

                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                            Datos Logísticos redactados
                          </p>
                          <div className="text-[11px] text-slate-600 dark:text-zinc-400 mt-0.5 space-y-0.5">
                            {esContenedor ? (
                              <>
                                <p>
                                  <strong className="text-slate-800 dark:text-zinc-200">Contenedor:</strong> {datos.contenedor_numero || datos.contenedorNumero || 'N/A'}{' '}
                                  {datos.contenedor_tipo || datos.contenedorTipo ? `(${datos.contenedor_tipo || datos.contenedorTipo})` : ''}
                                </p>
                                <p>
                                  <strong className="text-slate-800 dark:text-zinc-200">Sello SAT:</strong> {datos.sello_fiscal || datos.selloFiscal || 'N/A'} •{' '}
                                  <strong className="text-slate-800 dark:text-zinc-200">BL:</strong> {datos.booking || datos.bookingBL || 'N/A'}
                                </p>
                              </>
                            ) : (
                              <>
                                <p>
                                  <strong className="text-slate-800 dark:text-zinc-200">Almacén:</strong> {datos.almacen_destino || datos.almacenOrigen || 'Almacén Central'}
                                </p>
                                <p>
                                  <strong className="text-slate-800 dark:text-zinc-200">Placas:</strong> {datos.placas || datos.placasTransporte || 'N/A'} •{' '}
                                  <strong className="text-slate-800 dark:text-zinc-200">Chofer:</strong> {datos.operador || datos.operadorChofer || 'N/A'}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Botones de Aprobación / Rechazo */}
                      {esGestor && (
                        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100 dark:border-white/[0.04]">
                          <button
                            type="button"
                            onClick={() => {
                              setSolicitudParaRechazar(sol);
                              setMotivoRechazo('');
                            }}
                            disabled={procesandoId === sol.id_solicitud}
                            className="px-3.5 py-1.5 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-xs font-bold hover:bg-rose-100 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20 transition-colors cursor-pointer"
                          >
                            Rechazar con Retroalimentación
                          </button>

                          <button
                            type="button"
                            onClick={() => handleAprobarIndividual(sol.id_solicitud)}
                            disabled={procesandoId === sol.id_solicitud}
                            className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-500 shadow-sm transition-all hover:scale-[1.02] disabled:opacity-50 cursor-pointer"
                          >
                            <CheckIcon className="w-4 h-4" />
                            <span>{procesandoId === sol.id_solicitud ? 'Asentando...' : 'Aprobar y Asentar en Stock'}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          /* Pestaña: Elegir Estudiantes Supervisores (Roles de Validación) */
          <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
            <div className="p-4 rounded-2xl bg-indigo-50/70 border border-indigo-200/80 dark:bg-indigo-950/20 dark:border-indigo-500/30 flex items-start gap-3">
              <StarIcon className="w-6 h-6 text-amber-500 fill-amber-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-indigo-950 dark:text-indigo-200">
                  Delegación de Roles: Estudiantes Supervisores de Turno WMS
                </h4>
                <p className="text-xs text-indigo-800/80 dark:text-indigo-300/80 mt-1 leading-relaxed">
                  Elige a los alumnos que fungirán como <strong>Supervisores de Mesa de Control</strong> durante la práctica. Los estudiantes seleccionados podrán ingresar a la Mesa de Control y evaluar/aprobar los ingresos y retiros de sus compañeros como parte de su formación en roles logísticos.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {miembros.map((m) => {
                const uId = Number(m.id_usuario || m.id_miembro);
                const esSupervisor = supervisoresLocales.includes(uId);
                const esDocente = ['maestro', 'admin', 'jefe', 'supervisor'].includes((m.rol_miembro || '').toLowerCase());

                return (
                  <div
                    key={uId}
                    className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all ${
                      esSupervisor
                        ? 'border-indigo-500/50 bg-indigo-50/30 dark:border-indigo-500/40 dark:bg-indigo-950/20 shadow-xs'
                        : 'border-slate-200/70 bg-white dark:border-white/[0.06] dark:bg-[#121319]'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-200 font-bold text-xs uppercase">
                        {m.nombre?.[0] || 'U'}{m.apellido?.[0] || ''}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h5 className="text-xs font-bold text-slate-900 dark:text-white truncate">
                            {m.nombre} {m.apellido}
                          </h5>
                          {esSupervisor && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40">
                              ⭐ Supervisor
                            </span>
                          )}
                          {esDocente && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold uppercase bg-slate-200 text-slate-700 dark:bg-zinc-700 dark:text-zinc-300">
                              {m.rol_miembro}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-zinc-400 truncate">
                          {m.email}
                        </p>
                      </div>
                    </div>

                    {!esDocente ? (
                      <button
                        type="button"
                        disabled={guardandoSupervisores}
                        onClick={() => handleToggleSupervisorEstudiante(uId)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          esSupervisor
                            ? 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30'
                            : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-xs'
                        }`}
                      >
                        {esSupervisor ? 'Quitar Rol' : 'Designar Supervisor'}
                      </button>
                    ) : (
                      <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">
                        Docente (Nativo)
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Modal Secundario: Rechazo con retroalimentación */}
        {solicitudParaRechazar && (
          <div className="absolute inset-0 z-20 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#181920] border border-slate-200 dark:border-white/10">
              <div className="flex items-center gap-3 text-rose-600 dark:text-rose-400 mb-2">
                <ExclamationTriangleIcon className="w-6 h-6" />
                <h4 className="text-base font-bold text-slate-900 dark:text-white">
                  Rechazar Operación
                </h4>
              </div>
              <p className="text-xs text-slate-600 dark:text-zinc-400 mb-4">
                Indica la retroalimentación para que el estudiante comprenda el error o discrepancia:
              </p>
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                placeholder="Ejemplo: El contenedor no cuenta con el formato ISO válido o falta el sello fiscal SAT."
                rows={3}
                className="w-full rounded-xl border border-slate-300 p-3 text-xs text-slate-900 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSolicitudParaRechazar(null)}
                  className="px-3.5 py-1.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 dark:text-zinc-400 dark:hover:bg-zinc-800 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmarRechazo}
                  className="px-4 py-1.5 rounded-xl bg-rose-600 text-xs font-bold text-white hover:bg-rose-500 transition-colors shadow-sm cursor-pointer"
                >
                  Confirmar Rechazo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-slate-200/70 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] flex items-center justify-between">
          <span className="text-[11px] text-slate-500 dark:text-zinc-400">
            Módulo de Práctica • LogixPort WMS Enterprise • Simulador Dual Alumno / Supervisor
          </span>
          <button
            onClick={onCerrar}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-200 hover:bg-slate-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-200 transition-colors cursor-pointer"
          >
            Cerrar Mesa
          </button>
        </div>
      </div>
    </div>
  );
};
